import { v4 as uuidv4 } from 'uuid';
import { AudienceFilterType } from 'types/create-campaign';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

var _ = require('lodash');

const CREATE_NEW_AUDIENCE_STORE_NAME = 'create-new-audience-store';

type ChannelType = 'sms' | 'push';

type ChannelEstimateType = {
  estimated_reachable_users: number;
  has_credits: boolean;
  credit_cost: number;
};

type CreateNewAudienceStore = {
  name: string;
  estimatedAudience: Record<ChannelType, ChannelEstimateType | null>;
  audienceFilters: AudienceFilterType[][];
  actions: {
    setName: (data: string) => void;
    setEstimatedAudience: (
      data: Record<ChannelType, ChannelEstimateType | null>
    ) => void;
    addFilterToGroup: (id: string) => void;
    addFiltersGroup: () => void;
    removeFilterById: (id: string) => void;
    setAudienceFilters: (data: AudienceFilterType[]) => void;
    clearCreateNewAudienceStore: () => void;
    updateAttribute: (id: string, newAttributeValue: any) => void;
    updateOperator: (id: string, newOperatorValue: any) => void;
    updateValue: (id: string, newValue: any, ui_presentation?: string) => void;
  };
};

export const useCreateNewAudienceStore = create<CreateNewAudienceStore>()(
  devtools(
    persist(
      (set) => ({
        name: '',
        estimatedAudience: {
          sms: null,
          push: null,
        },
        audienceFilters: [[]],
        actions: {
          setName: (data: string) => set({ name: data }),
          setEstimatedAudience: (
            data: Record<ChannelType, ChannelEstimateType | null>
          ) => set({ estimatedAudience: data }),
          addFilterToGroup: (id: string) => {
            set((state) => {
              const filtersGroupIndex = state.audienceFilters.findIndex(
                (filtersGroup) =>
                  filtersGroup.some((filter) => filter.id === id)
              );
              if (filtersGroupIndex !== -1) {
                const newState: CreateNewAudienceStore = _.cloneDeep(state);
                newState.audienceFilters[filtersGroupIndex].push({
                  id: uuidv4(),
                  // @ts-ignore
                  attribute: '',
                  operator: '',
                  value: '',
                });
                return newState;
              }
              return state;
            });
          },
          addFiltersGroup: () => {
            set((state) => {
              const newState: CreateNewAudienceStore = _.cloneDeep(state);
              newState.audienceFilters.push([
                {
                  id: uuidv4(),
                  // @ts-ignore
                  attribute: '',
                  operator: '',
                  value: '',
                },
              ]);
              return newState;
            });
          },
          removeFilterById: (id: string) => {
            set((state) => {
              const newState: CreateNewAudienceStore = _.cloneDeep(state);

              for (let i = 0; i < newState.audienceFilters.length; i++) {
                const filtersGroup = newState.audienceFilters[i];
                const filterIndex = filtersGroup.findIndex(
                  (filter) => filter.id === id
                );

                if (filterIndex !== -1) {
                  // Remove the filter
                  filtersGroup.splice(filterIndex, 1);

                  // If the filtersGroup becomes empty, remove the entire filtersGroup
                  if (filtersGroup.length === 0) {
                    newState.audienceFilters.splice(i, 1);
                  }

                  return newState;
                }
              }

              return state;
            });
          },
          setAudienceFilters: (data: AudienceFilterType[]) =>
            set((state) => {
              return {
                audienceFilters: [...state.audienceFilters, data],
              };
            }),
          clearCreateNewAudienceStore: () => {
            return set({
              name: '',
              audienceFilters: [
                [
                  {
                    id: uuidv4(),
                    // @ts-ignore
                    attribute: '',
                    operator: '',
                    value: '',
                  },
                ],
              ],
              estimatedAudience: {
                sms: null,
                push: null,
              },
            });
          },
          updateAttribute: (
            id: string,
            newAttributeValue: AudienceFilterType['attribute']
          ) => {
            set((state) => {
              const updatedFilters = state.audienceFilters.map((filtersGroup) =>
                filtersGroup.map((filter) =>
                  filter.id === id
                    ? { ...filter, attribute: newAttributeValue }
                    : filter
                )
              );
              return { audienceFilters: updatedFilters };
            });
          },
          updateOperator: (
            id: string,
            newOperatorValue: AudienceFilterType['operator']
          ) => {
            set((state) => {
              const updatedFilters = state.audienceFilters.map((filtersGroup) =>
                filtersGroup.map((filter) =>
                  filter.id === id
                    ? { ...filter, operator: newOperatorValue }
                    : filter
                )
              );
              return { audienceFilters: updatedFilters };
            });
          },
          updateValue: (
            id: string,
            newValue: AudienceFilterType['value'],
            ui_presentation?: string
          ) =>
            set((state) => {
              const updatedFilters = state.audienceFilters.map((filtersGroup) =>
                filtersGroup.map((filter) =>
                  filter.id === id
                    ? {
                        ...filter,
                        value: newValue,
                        ui_presentation: ui_presentation || null,
                      }
                    : filter
                )
              );

              return {
                ...state,
                audienceFilters: updatedFilters,
              };
            }),
        },
      }),
      {
        name: CREATE_NEW_AUDIENCE_STORE_NAME,
        partialize: (state) => ({
          name: state.name,
          estimatedAudience: state.estimatedAudience,
          audienceFilters: state.audienceFilters,
        }),
      }
    ),
    {
      name: CREATE_NEW_AUDIENCE_STORE_NAME,
    }
  )
);

export const useCreateNewAudienceActions = () =>
  useCreateNewAudienceStore((state) => state.actions);
